import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { Copy, Download, Calendar, ArrowLeft, Target, TrendingUp, AlertTriangle, Rocket, DollarSign, Users, Lightbulb, Map, Zap, Home } from 'lucide-react';
import { useAuthCheck } from '../hooks/useAuthCheck';

function CategoryPageNew() {
    useAuthCheck();

    const { categoryKey } = useParams();
    const navigate = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [ideaData, setIdeaData] = useState(null);
    const [currentSection, setCurrentSection] = useState(null);


    // Complete analysis sections mapping for new business ideas
    const analysisSections = [
        { title: "Deep Market Analysis", icon: <TrendingUp size={20} />, key: "marketAnalysis", color: "blue" },
        { title: "Target Audience Research", icon: <Users size={20} />, key: "audienceResearch", color: "green" },
        { title: "Required Resources", icon: <Zap size={20} />, key: "requiredResources", color: "yellow" },
        { title: "Cost Estimation", icon: <DollarSign size={20} />, key: "costEstimation", color: "emerald" },
        { title: "Competitor Breakdown", icon: <Target size={20} />, key: "competitorBreakdown", color: "red" },
        { title: "Execution Strategy", icon: <Rocket size={20} />, key: "executionStrategy", color: "purple" },
        { title: "Unique Selling Points", icon: <Lightbulb size={20} />, key: "uniqueSellingPoints", color: "indigo" },
        { title: "Risk Analysis", icon: <AlertTriangle size={20} />, key: "riskAnalysis", color: "orange" },
        { title: "Monetization Plan", icon: <DollarSign size={20} />, key: "monetizationPlan", color: "teal" },
        { title: "Full Roadmap", icon: <Map size={20} />, key: "roadmap", color: "pink" },
        { title: "Business Growth Strategy", icon: <TrendingUp size={20} />, key: "growthStrategy", color: "cyan" },
        { title: "Chat About Your Idea", icon: <Users size={20} />, key: "chatSupport", color: "gray" },
    ];

    // Complete color classes function
    const getColorClasses = (color) => {
        const colors = {
            blue: { border: 'border-blue-200', bg: 'bg-blue-50', hover: 'hover:bg-blue-100', text: 'text-blue-700', button: 'btn-outline-primary', cardbg: '#dbeafe', cardborder: '#93c5fd', cardtext: '#1e40af' },
            green: { border: 'border-green-200', bg: 'bg-green-50', hover: 'hover:bg-green-100', text: 'text-green-700', button: 'btn-outline-success', cardbg: '#dcfce7', cardborder: '#86efac', cardtext: '#166534' },
            yellow: { border: 'border-yellow-200', bg: 'bg-yellow-50', hover: 'hover:bg-yellow-100', text: 'text-yellow-700', button: 'btn-outline-warning', cardbg: '#fef9c3', cardborder: '#fde047', cardtext: '#854d0e' },
            emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', hover: 'hover:bg-emerald-100', text: 'text-emerald-700', button: 'btn-outline-success', cardbg: '#d1fae5', cardborder: '#6ee7b7', cardtext: '#065f46' },
            red: { border: 'border-red-200', bg: 'bg-red-50', hover: 'hover:bg-red-100', text: 'text-red-700', button: 'btn-outline-danger', cardbg: '#fee2e2', cardborder: '#fca5a5', cardtext: '#991b1b' },
            purple: { border: 'border-purple-200', bg: 'bg-purple-50', hover: 'hover:bg-purple-100', text: 'text-purple-700', button: 'btn-outline-primary', cardbg: '#f3e8ff', cardborder: '#c4b5fd', cardtext: '#6b21a8' },
            indigo: { border: 'border-indigo-200', bg: 'bg-indigo-50', hover: 'hover:bg-indigo-100', text: 'text-indigo-700', button: 'btn-outline-primary', cardbg: '#e0e7ff', cardborder: '#a5b4fc', cardtext: '#3730a3' },
            orange: { border: 'border-orange-200', bg: 'bg-orange-50', hover: 'hover:bg-orange-100', text: 'text-orange-700', button: 'btn-outline-warning', cardbg: '#ffedd5', cardborder: '#fdba74', cardtext: '#9a3412' },
            teal: { border: 'border-teal-200', bg: 'bg-teal-50', hover: 'hover:bg-teal-100', text: 'text-teal-700', button: 'btn-outline-info', cardbg: '#ccfbf1', cardborder: '#5eead4', cardtext: '#134e4a' },
            pink: { border: 'border-pink-200', bg: 'bg-pink-50', hover: 'hover:bg-pink-100', text: 'text-pink-700', button: 'btn-outline-danger', cardbg: '#fce7f3', cardborder: '#f9a8d4', cardtext: '#831843' },
            cyan: { border: 'border-cyan-200', bg: 'bg-cyan-50', hover: 'hover:bg-cyan-100', text: 'text-cyan-700', button: 'btn-outline-info', cardbg: '#cffafe', cardborder: '#5eead4', cardtext: '#164e63' },
            gray: { border: 'border-gray-200', bg: 'bg-gray-50', hover: 'hover:bg-gray-100', text: 'text-gray-700', button: 'btn-outline-secondary', cardbg: '#f3f4f6', cardborder: '#d1d5db', cardtext: '#374151' },
        };
        return colors[color] || colors.gray;
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [categoryKey]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const section = analysisSections.find(s => s.key === categoryKey);
                setCurrentSection(section);
                if (!section) { console.error('Section not found for key:', categoryKey); navigate('/new'); return; }

                const savedAnalysis = localStorage.getItem('newBusinessAnalysis');
                const savedFormData = localStorage.getItem('newBusinessFormData');
                if (!savedAnalysis || !savedFormData) { console.warn('No data found in localStorage for new business'); navigate('/new'); return; }

                const parsedAnalysis = JSON.parse(savedAnalysis);
                const parsedFormData = JSON.parse(savedFormData);
                if (!parsedAnalysis[categoryKey]) { console.warn(`No data found for section: ${categoryKey}`); }

                setAnalysis(parsedAnalysis);
                setIdeaData(parsedFormData);
            } catch (error) {
                console.error('Error loading data:', error);
                navigate('/new');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [categoryKey]);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // renderTableHTML helper — used by formatTextForPDF
    // ─────────────────────────────────────────────────────────────────────────
    const renderTableHTML = (rows) => {
        if (!rows || rows.length === 0) return '';

        let tableHTML = '<table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 13px; border: 1px solid #e5e7eb;">';

        rows.forEach((row, rowIndex) => {
            // Skip separator rows
            if (row.replace(/[\|\-\s=]/g, '') === '') return;

            const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
            if (cells.length === 0) return;

            const isHeader = rowIndex === 0;
            const tag = isHeader ? 'th' : 'td';
            const rowBg = isHeader ? '#eff6ff' : (rowIndex % 2 === 0 ? '#ffffff' : '#f9fafb');

            tableHTML += `<tr style="background: ${rowBg};">`;
            cells.forEach((cell) => {
                const style = isHeader
                    ? 'background: #eff6ff; font-weight: 600; color: #1e40af; border: 1px solid #bfdbfe; padding: 12px 15px; text-align: left;'
                    : 'border: 1px solid #e5e7eb; padding: 10px 14px; text-align: left; vertical-align: top;';
                tableHTML += `<${tag} style="${style}">${cell}</${tag}>`;
            });
            tableHTML += '</tr>';
        });

        tableHTML += '</table>';
        return tableHTML;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // formatTextForPDF — FIXED: table ke baad ka content bhi render hota hai
    // ─────────────────────────────────────────────────────────────────────────
    const formatTextForPDF = (text) => {
        if (!text) return '';

        const lines = text.split('\n');
        let formattedHTML = '';
        let i = 0;

        while (i < lines.length) {
            const line = lines[i];
            const trimmed = line.trim();
            const nextTrimmed = (lines[i + 1] || '').trim();

            // ── Detect table: current line has pipes AND next line is separator ──
            const isTableHeader =
                trimmed.includes('|') &&
                (nextTrimmed.includes('---') || nextTrimmed.includes('==='));

            if (isTableHeader) {
                const tableLines = [];
                while (i < lines.length && lines[i].trim().includes('|')) {
                    tableLines.push(lines[i].trim());
                    i++;
                }
                formattedHTML += renderTableHTML(tableLines);
                continue; // i already moved — do NOT increment
            }

            // ── Empty line ──
            if (!trimmed) {
                formattedHTML += '<div style="height: 8px;"></div>';
                i++;
                continue;
            }

            // ── Heading: ends with colon, short, no bullet/number ──
            if (trimmed.endsWith(':') && trimmed.length < 100 && !trimmed.match(/^[•*\-+\d]/)) {
                formattedHTML += `<h3 style="color: #0d53bd; margin: 25px 0 15px 0; font-size: 18px; font-weight: 700;">${trimmed}</h3>`;
                i++;
                continue;
            }

            // ── ALL-CAPS heading ──
            const wordsCount = trimmed.split(' ').length;
            const isMostlyCaps = trimmed === trimmed.toUpperCase();
            if (isMostlyCaps && trimmed.length < 100 && wordsCount >= 2 && wordsCount <= 8 && !trimmed.includes('.') && !trimmed.match(/^[•*\-+\d]/)) {
                formattedHTML += `<h3 style="color: #0d53bd; margin: 25px 0 15px 0; font-size: 18px; font-weight: 700;">${trimmed}</h3>`;
                i++;
                continue;
            }

            // ── Numbered item ──
            const numberedMatch = trimmed.match(/^(\d+\.)\s+(.+)/);
            if (numberedMatch) {
                const [, number, content] = numberedMatch;
                formattedHTML += `
                <div style="margin: 15px 0; padding-left: 20px; display: flex; align-items: flex-start;">
                    <span style="background: #0d53bd; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; margin-right: 10px; font-size: 12px; font-weight: bold; flex-shrink: 0;">
                        ${number.replace('.', '')}
                    </span>
                    <span>${content}</span>
                </div>`;
                i++;
                continue;
            }

            // ── Bullet point ──
            const bulletMatch = trimmed.match(/^[•*\-]\s+(.+)/);
            if (bulletMatch) {
                const bulletContent = bulletMatch[1];
                const colonIdx = bulletContent.indexOf(':');
                const hasColonInMiddle = colonIdx > 0 && colonIdx < bulletContent.length - 1;

                if (hasColonInMiddle) {
                    const before = bulletContent.substring(0, colonIdx + 1);
                    const after = bulletContent.substring(colonIdx + 1).trim();
                    formattedHTML += `
                    <div style="margin: 8px 0; padding-left: 20px; display: flex; align-items: flex-start;">
                        <span style="color: #0d53bd; margin-right: 10px; font-weight: bold; flex-shrink: 0;">•</span>
                        <span><strong>${before}</strong> ${after}</span>
                    </div>`;
                } else {
                    formattedHTML += `
                    <div style="margin: 8px 0; padding-left: 20px; display: flex; align-items: flex-start;">
                        <span style="color: #0d53bd; margin-right: 10px; font-weight: bold; flex-shrink: 0;">•</span>
                        <span>${bulletContent}</span>
                    </div>`;
                }
                i++;
                continue;
            }

            // ── Key: Value line ──
            const colonIndex = trimmed.indexOf(':');
            if (colonIndex > 0 && colonIndex < trimmed.length - 1) {
                const before = trimmed.substring(0, colonIndex + 1);
                const after = trimmed.substring(colonIndex + 1).trim();
                formattedHTML += `
                <div style="margin: 15px 0;">
                    <strong style="color: #0d53bd;">${before}</strong>
                    <div style="margin-left: 20px; margin-top: 5px;">${after}</div>
                </div>`;
                i++;
                continue;
            }

            // ── Regular paragraph ──
            formattedHTML += `<p style="margin: 12px 0; line-height: 1.7;">${trimmed}</p>`;
            i++;
        }

        return formattedHTML;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // downloadAsPDF — single section
    // ─────────────────────────────────────────────────────────────────────────
    const downloadAsPDF = async (sectionTitle, content) => {
        try {
            let htmlContent;
            if (typeof content === 'string') {
                htmlContent = formatTextForPDF(content);
            } else {
                htmlContent = extractContentAsHTML(formattedContent || content);
            }

            const element = document.createElement('div');
            element.innerHTML = `
            <div style="padding: 40px; max-width: 800px; margin: 0 auto;">
                <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #0d53bd;">
                    <h1 style="color: #0d53bd; font-size: 28px; margin-bottom: 10px; font-weight: 700;">${sectionTitle}</h1>
                    <div style="display: flex; justify-content: center; gap: 30px; margin-top: 15px; color: #666; font-size: 14px;">
                        <div><strong>Business:</strong> ${ideaData?.idea || 'Your Business Idea'}</div>
                        <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                </div>
                <div style="font-size: 14px; line-height: 1.7; color: #444;">${htmlContent}</div>
                <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
                    <p>Generated by InsightDraft AI • Confidential Business Analysis</p>
                    <p style="margin-top: 5px;">This document is for informational purposes only.</p>
                </div>
            </div>`;

            const options = {
                margin: 10,
                filename: `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true, windowWidth: 800 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };

            await html2pdf().set(options).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('PDF generation failed. Downloading as HTML instead.');
            downloadAsHTML(sectionTitle, content);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // downloadAsHTML — fallback HTML download (original function preserved)
    // ─────────────────────────────────────────────────────────────────────────
    const downloadAsHTML = (sectionTitle, content) => {
        try {
            let htmlContent;
            if (typeof content === 'string') {
                htmlContent = formatTextForPDF(content);
            } else {
                htmlContent = extractContentAsHTML(formattedContent || content);
            }

            const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <title>${sectionTitle}</title>
    <meta charset="UTF-8">
    <style>
        body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
        h1 { color: #0d53bd; border-bottom: 2px solid #0d53bd; padding-bottom: 10px; }
        .content { margin-top: 30px; }
        .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
    </style>
</head>
<body>
    <h1>${sectionTitle}</h1>
    <div><strong>Business:</strong> ${ideaData?.idea || 'Your Business Idea'}</div>
    <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
    <div class="content">${htmlContent}</div>
    <div class="footer">Generated by InsightDraft AI • Confidential Business Analysis</div>
</body>
</html>`;

            const blob = new Blob([fullHTML], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading HTML:', error);
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // extractContentAsHTML — helper to extract HTML from React elements (original)
    // ─────────────────────────────────────────────────────────────────────────
    const extractContentAsHTML = (content) => {
        if (!content) return '';
        if (typeof content === 'string') return content;
        if (content.props && content.props.dangerouslySetInnerHTML) {
            return content.props.dangerouslySetInnerHTML.__html || '';
        }
        if (content.props && content.props.children) {
            return String(content.props.children);
        }
        return String(content);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // renderFormattedContent — FIXED: table ke baad remaining content bhi show hoti hai
    // ─────────────────────────────────────────────────────────────────────────
    const renderFormattedContent = (text) => {
        if (!text) return null;

        // ✅ Pehle **bold** formatting ko HTML <strong> mein convert karo
        const processBoldText = (content) => {
            return content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        };

        const renderContent = (content) => {
            // ✅ Bold formatting apply karo before processing
            const processedContent = processBoldText(content);
            const lines = processedContent.split('\n');
            const elements = [];
            let i = 0;

            while (i < lines.length) {
                const line = lines[i];
                const trimmed = line.trim();
                const nextLine = (lines[i + 1] || '').trim();

                const isTableHeader = trimmed.includes('|') && (nextLine.includes('---') || nextLine.includes('==='));

                if (isTableHeader) {
                    const tableLines = [];
                    while (i < lines.length && lines[i].trim().includes('|')) {
                        tableLines.push(lines[i].trim());
                        i++;
                    }

                    const headerCells = tableLines[0].split('|').map(c => c.trim()).filter(Boolean);
                    const dataRows = tableLines.slice(2).filter(row => row.replace(/[\|\-\s=]/g, '') !== '');

                    elements.push(
                        <div key={`table-${i}`} style={{ overflowX: 'auto', margin: '16px 0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', border: '1px solid #e5e7eb' }}>
                                <thead>
                                    <tr style={{ background: '#eff6ff', borderBottom: '2px solid #bfdbfe' }}>
                                        {headerCells.map((header, ci) => (
                                            <th key={ci} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#1e40af' }}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataRows.map((row, ri) => {
                                        const cells = row.split('|').map(c => c.trim()).filter(Boolean);
                                        return (
                                            <tr key={ri} style={{ borderBottom: '1px solid #e5e7eb', background: ri % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                                {cells.map((cell, ci) => (
                                                    <td key={ci} style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                                                        <span dangerouslySetInnerHTML={{ __html: cell }} />
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                    continue;
                }

                if (!trimmed) {
                    elements.push(<div key={i} style={{ height: '8px' }} />);
                    i++; continue;
                }

                const hasNoBulletOrNumber = !trimmed.match(/^[•*\-+\d]/);

                if (trimmed.endsWith(':') && hasNoBulletOrNumber) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            <span dangerouslySetInnerHTML={{ __html: trimmed }} />
                        </div>
                    );
                    i++; continue;
                }

                const colonIdxR2 = trimmed.indexOf(':');
                const isShortLine = trimmed.length < 80;
                const hasNoPeriodAtEnd = !trimmed.endsWith('.');
                const wordsCountR2 = trimmed.split(' ').length;
                if (colonIdxR2 > 0 && colonIdxR2 === trimmed.length - 1 && isShortLine && hasNoPeriodAtEnd && wordsCountR2 <= 10) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            <span dangerouslySetInnerHTML={{ __html: trimmed }} />
                        </div>
                    );
                    i++; continue;
                }

                const isMostlyCaps = trimmed === trimmed.toUpperCase();
                const wordsCount = trimmed.split(' ').length;
                const isHeadingLike = isMostlyCaps && trimmed.length < 100 && wordsCount >= 2 && wordsCount <= 8 && !trimmed.includes('.') && !trimmed.includes(',');
                if (isHeadingLike && hasNoBulletOrNumber) {
                    elements.push(
                        <div key={i} style={{ fontWeight: '700', marginBottom: '12px', fontSize: '1.05em', marginTop: i > 0 ? '8px' : '0', textAlign: 'left' }}>
                            <span dangerouslySetInnerHTML={{ __html: trimmed }} />
                        </div>
                    );
                    i++; continue;
                }

                const numberedMatch = trimmed.match(/^(\d+\.)\s+(.+)/);
                if (numberedMatch) {
                    const [, number, text] = numberedMatch;
                    elements.push(
                        <div key={i} style={{ marginBottom: '12px' }}>
                            <div style={{ fontWeight: '700', fontSize: '1.05em', display: 'flex', gap: '8px', textAlign: 'left' }}>
                                <span>{number}</span>
                                <span dangerouslySetInnerHTML={{ __html: text }} />
                            </div>
                        </div>
                    );
                    i++; continue;
                }

                const bulletMatch = trimmed.match(/^[•*\-+]\s+(.+)/);
                if (bulletMatch && bulletMatch[1].endsWith(':')) {
                    const content = bulletMatch[1];
                    elements.push(
                        <div key={i} style={{ marginBottom: '12px', marginLeft: '24px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', fontWeight: '600' }}>•</div>
                                <div style={{ flex: 1, fontWeight: '700', fontSize: '1.05em' }}>
                                    <span dangerouslySetInnerHTML={{ __html: content }} />
                                </div>
                            </div>
                        </div>
                    );
                    i++; continue;
                }

                if (bulletMatch) {
                    const bulletContent = bulletMatch[1];
                    const hasColonInMiddle = bulletContent.includes(':') && !bulletContent.endsWith(':');

                    if (hasColonInMiddle) {
                        const cIdx = bulletContent.indexOf(':');
                        const before = bulletContent.substring(0, cIdx + 1);
                        const after = bulletContent.substring(cIdx + 1).trim();
                        elements.push(
                            <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '8px', marginLeft: '24px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', marginTop: '2px' }}>•</div>
                                <div style={{ flex: 1 }}>
                                    <strong dangerouslySetInnerHTML={{ __html: before }} /> <span dangerouslySetInnerHTML={{ __html: after }} />
                                </div>
                            </div>
                        );
                    } else {
                        elements.push(
                            <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'flex-start', textAlign: 'left' }}>
                                <div style={{ minWidth: '16px', marginTop: '2px' }}>•</div>
                                <div style={{ flex: 1 }}>
                                    <span dangerouslySetInnerHTML={{ __html: bulletContent }} />
                                </div>
                            </div>
                        );
                    }
                    i++; continue;
                }

                if (trimmed.includes(':') && !trimmed.endsWith(':')) {
                    const colonIndex = trimmed.indexOf(':');
                    const beforeColon = trimmed.substring(0, colonIndex + 1);
                    const afterColon = trimmed.substring(colonIndex + 1).trim();
                    if (afterColon) {
                        elements.push(
                            <div key={i} style={{ marginBottom: '12px' }}>
                                <div style={{ fontWeight: '700', marginBottom: '4px', textAlign: 'left' }}>
                                    <span dangerouslySetInnerHTML={{ __html: beforeColon }} />
                                </div>
                                <div style={{ marginLeft: '20px', textAlign: 'left' }}>
                                    <span dangerouslySetInnerHTML={{ __html: afterColon }} />
                                </div>
                            </div>
                        );
                        i++; continue;
                    }
                }

                elements.push(
                    <div key={i} style={{ marginBottom: '8px', lineHeight: '1.6', textAlign: 'left' }}>
                        <span dangerouslySetInnerHTML={{ __html: trimmed }} />
                    </div>
                );
                i++;
            }

            return <div style={{ margin: '16px 0' }}>{elements}</div>;
        };

        return renderContent(text);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // LOADING / ERROR STATES
    // ─────────────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
                    <h5 className="text-primary" style={{ fontSize: '20px' }}>Loading Analysis...</h5>
                </div>
            </div>
        );
    }

    if (!currentSection || !analysis || !ideaData) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <h3 className="text-danger mb-3">Analysis Not Found</h3>
                    <p className="mb-3">Please generate analysis first from the new business idea page.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/new')}>
                        Go Back to Generate Analysis
                    </button>
                </div>
            </div>
        );
    }

    const colorClass = getColorClasses(currentSection.color);
    const content = analysis[currentSection.key] || 'No analysis content available for this section.';
    const formattedContent = renderFormattedContent(content);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    // ... (all imports remain same until RENDER section)

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            <div className="container-fluid py-3 py-md-5 px-2 px-md-4" style={{ maxWidth: 1400 }}>

                {/* Header - Responsive */}
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-3 mb-md-5 px-2">
                    {/* Back button */}
                    <button
                        className={`btn d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                        onClick={() => navigate(-1)}
                        style={{
                            borderRadius: '12px',
                            border: `2px solid ${colorClass?.cardtext || '#1e40af'}`,
                            padding: 'clamp(8px, 2vw, 12px) clamp(16px, 3vw, 24px)',
                            fontSize: 'clamp(14px, 2.5vw, 16px)',
                            transition: 'all 0.3s ease',
                            color: colorClass?.cardtext || '#1e40af',
                            backgroundColor: 'transparent',
                            alignSelf: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <ArrowLeft size={18} className="me-2" />
                        Back
                    </button>

                    {/* Title - Centered */}
                    <div className="text-center flex-grow-1">
                        <h1 className="fw-bold mb-1" style={{
                            color: colorClass?.cardtext || '#1e40af',
                            fontSize: 'clamp(1.2rem, 4vw, 2rem)',
                            lineHeight: '1.2',
                            wordBreak: 'break-word'
                        }}>
                            {currentSection?.title}
                        </h1>
                        <p className="text-muted m-0" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)' }}>
                            Analysis For: <strong style={{ color: colorClass?.text }}>"{ideaData?.idea || 'Your Business Idea'}"</strong>
                        </p>
                    </div>

                    {/* Spacer for balance */}
                    <div className="d-none d-md-block" style={{ width: '100px' }}></div>
                </div>

                {/* Main Content - Responsive Container */}
                <div className="px-0 px-md-3">
                    <div className="mx-auto" style={{
                        maxWidth: '100%',
                        borderRadius: 'clamp(8px, 2vw, 16px)',
                        overflow: 'hidden'
                    }}>
                        {/* Content Card - Mobile: no border, full width */}
                        <div className="p-2 p-md-3" style={{
                            background: '#f8fafc',
                            minHeight: 'clamp(300px, 60vh, 600px)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div className="p-3 p-md-4 rounded-3" style={{
                                background: 'white',
                                border: window.innerWidth > 768 ? `1px solid ${colorClass?.cardborder || '#93c5fd'}` : 'none',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                boxShadow: window.innerWidth > 768 ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                            }}>
                                {/* Scrollable Content */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    paddingRight: '8px',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: `${colorClass?.cardborder || '#93c5fd'} #f0f0f0`
                                }}>
                                    <div style={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'inherit',
                                        fontSize: 'clamp(14px, 2vw, 16px)',
                                        lineHeight: '1.7',
                                        color: '#374151',
                                        textAlign: 'left',
                                        padding: '4px'
                                    }}>
                                        {formattedContent}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer - Action Buttons - Responsive */}
                        <div className="p-2 p-md-3 d-flex justify-content-between align-items-center"
                            style={{
                                background: 'white',
                                borderTop: window.innerWidth > 768 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                            }}>
                            {/* Left side - Date and Brand */}
                            <div className="d-flex align-items-center gap-2 gap-md-3">
                                <small className="text-muted d-flex align-items-center" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                                    <Calendar size={window.innerWidth > 576 ? 16 : 14} className="me-1 me-md-2" />
                                    {new Date().toLocaleDateString('en-US', {
                                        month: window.innerWidth > 576 ? 'long' : 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </small>
                                <small className="text-muted d-none d-sm-block" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                                    InsightDraft AI
                                </small>
                            </div>

                            {/* ✅ Right side - Action Buttons */}
                            <div className="d-flex gap-2 gap-md-3">
                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{
                                        borderRadius: '8px',
                                        background: colorClass?.cardbg || '#dbeafe',
                                        color: colorClass?.cardtext || '#1e40af',
                                        border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                                        fontSize: 'clamp(13px, 2vw, 15px)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => copyToClipboard(formattedContent?.props?.dangerouslySetInnerHTML?.__html || content)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                                        e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                                    }}
                                >
                                    <Copy size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {copied ? 'Copied!' : (window.innerWidth > 576 ? 'Copy Text' : 'Copy')}
                                </button>

                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{
                                        borderRadius: '8px',
                                        background: colorClass?.cardbg || '#dbeafe',
                                        color: colorClass?.cardtext || '#1e40af',
                                        border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                                        fontSize: 'clamp(13px, 2vw, 15px)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => downloadAsPDF(currentSection?.title, content)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                                        e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                                    }}
                                >
                                    <Download size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {window.innerWidth > 576 ? 'Download PDF' : 'PDF'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back to Full Analysis Button */}
                <div className="text-center mt-4 mt-md-5">
                    <button
                        className={`btn px-4 px-md-5 py-2 py-md-3`}
                        style={{
                            borderRadius: '12px',
                            background: colorClass?.cardbg || '#dbeafe',
                            color: colorClass?.cardtext || '#1e40af',
                            border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                            fontSize: 'clamp(14px, 2.5vw, 16px)',
                            transition: 'all 0.3s ease'
                        }}
                        onClick={() => {
                            const savedType = localStorage.getItem('analysisType') || 'new';
                            navigate(`/analysis?type=${savedType}`);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                            e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <ArrowLeft size={18} className="me-2" />
                        Back to Full Analysis
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CategoryPageNew;